import type { SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ViewChild,
  output,
  inject,
  input,
} from '@angular/core';
import { UntypedFormControl, ReactiveFormsModule } from '@angular/forms';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { TimePickerDropdownComponent } from '@core/time-ui';
import {
  ConnectedOverlayDirective,
  KeyCode,
  InputComponent,
  OverlayOriginDirective,
} from '@core/user-interface-legacy';

@Component({
  selector: 'rt-schedule-duration-picker',
  templateUrl: 'schedule-duration-picker.component.html',
  styleUrls: ['./schedule-duration-picker.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    OverlayOriginDirective,
    InputComponent,
    ReactiveFormsModule,
    ConnectedOverlayDirective,
    TimePickerDropdownComponent,
    TranslatePipe,
  ],
})
export class ScheduleDurationPickerComponent {
  private readonly i18n = inject(I18nService);
  private readonly cdRef = inject(ChangeDetectorRef);

  public readonly dropdownWidth: number = 120;

  public readonly dropdownMaxHeight = input<number>(300);

  public readonly underlined = input<boolean>(false);
  public readonly duration = input<string | number | null>(null);
  public readonly noLeftPadding = input<boolean>(false);
  public readonly invalid = input<boolean>(false);

  public durationChange = output<string>();

  public durationControl: UntypedFormControl;
  public currentDuration: string = '';
  public changedManually: boolean = false;

  @ViewChild('durationDropdown', { static: true })
  private durationDropdown: ConnectedOverlayDirective;

  public ngOnInit(): void {
    const duration = this.duration();
    if (duration !== null) {
      this.currentDuration = duration.toString();
    }

    this.durationControl = new UntypedFormControl(this.currentDuration);
    this.cdRef.detectChanges();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { duration } = changes;

    if (duration && duration.currentValue && this.durationControl) {
      this.currentDuration = duration.currentValue.toString();
      this.durationControl.setValue(this.currentDuration, { emitEvent: false });
    }
  }

  public onInputChange(value: string): void {
    this.changedManually = true;
    this.currentDuration = value;
    this.cdRef.detectChanges();
    this.durationChange.emit(this.currentDuration);
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (this.changedManually && event.keyCode === KeyCode.Enter) {
      event.preventDefault();
      event.stopPropagation();
      this.closeDropdown(this.durationDropdown);
    }

    if (
      this.changedManually &&
      (event.keyCode === KeyCode.DownArrow || event.keyCode === KeyCode.UpArrow)
    ) {
      this.changedManually = false;
      this.cdRef.detectChanges();
    }
  }

  public onDurationChange(duration: string): void {
    const replaceSearchValue = ' ' + this.i18n.translate('app.interview.schedule_dialog.minutes');
    const time = (duration || '').replace(replaceSearchValue, '');

    this.durationControl.setValue(time);
    this.currentDuration = time;
    this.cdRef.detectChanges();
    this.durationChange.emit(time);
  }

  public toggleDropdown(dropdown: ConnectedOverlayDirective): void {
    dropdown.open = !dropdown.open;
    this.cdRef.detectChanges();
  }

  public closeDropdown(dropdown: ConnectedOverlayDirective): void {
    dropdown.open = false;
    this.cdRef.detectChanges();
  }
}
